import { escapeHtml } from './escapeHtml.js';
/**
 * Pad a value to get a certain length (def: 2) with something (def: '0') on the left
 */
export function zpad(
/**
 * Input value
 */
v, 
/**
 * Minimum length
 *
 * @defaultValue 2
 */
n = 2, 
/**
 * Padding value
 *
 * @defaultValue '0'
 */
p = '0') {
    v = '' + v;
    while (v.length < n)
        v = p + v;
    return v;
}
/**
 * Formats a date so that it's readable by a normal user
 * Format: `DD/MM/YYYY`
 */
export function fmtDate(date) {
    if (!(date instanceof Date))
        date = new Date(date);
    return `${zpad(date.getDate())}/${zpad(date.getMonth() + 1)}/${zpad(date.getFullYear())}`;
}
/**
 * Formats a time so that it's readable by a normal user
 * Format: `hh:mm:ss`
 */
export function fmtTime(date) {
    if (!(date instanceof Date))
        date = new Date(date);
    return `${zpad(date.getHours())}:${zpad(date.getMinutes())}:${zpad(date.getSeconds())}`;
}
/**
 * Formats a date in long format
 * Format: `<depends on locale>`
 */
export function fmtDateLocaleLong(date, { locale = 'fr-FR', includeDay = true, } = {}) {
    if (!(date instanceof Date))
        date = new Date(date);
    return date.toLocaleDateString(locale, {
        month: 'long',
        year: 'numeric',
        day: includeDay ? 'numeric' : undefined,
    });
}
/**
 * Formats a date and time so that it's readable by a normal user
 * Format: `DD/MM/YYYY hh:mm:ss`
 */
export function fmtDateTime(date) {
    return `${fmtDate(date)} ${fmtTime(date)}`;
}
/**
 * Formats a date so that it's readable by a normal computer
 * Format: `YYYY-MM-DD`
 */
export function isoDate(date) {
    if (!(date instanceof Date))
        date = new Date(date);
    return `${zpad(date.getFullYear(), 4)}-${zpad(date.getMonth() + 1)}-${zpad(date.getDate())}`;
}
/**
 * Formats a date and time so that it's readable by a normal computer
 * Format: `YYYY-MM-DDTHH:MM:SS.mmmZ`
 */
export function isoDateTime(date) {
    if (!(date instanceof Date))
        date = new Date(date);
    return date.toISOString();
}
/**
 * Formats a time so that it's readable by a normal computer
 * Format: `HH:MM`
 */
export function isoTime(date) {
    if (!(date instanceof Date))
        date = new Date(date);
    return date.toISOString().split('T')[1].slice(0, 5);
}
/**
 * Formats a date in navitia format
 * Format: `YYYYMMDDThhmm00`
 */
export function navitiaDate(date) {
    if (!(date instanceof Date))
        date = new Date(date);
    return `${zpad(date.getFullYear(), 4)}${zpad(date.getMonth() + 1)}${zpad(date.getDate())}T${zpad(date.getHours())}${zpad(date.getMinutes())}00`;
}
/**
 * Parses a date in navitia format
 */
export function parseNavitiaDate(navitia) {
    const match = navitia.match(/^(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})$|$)/);
    if (!match)
        throw new Error('Not a navitia date');
    const [, yyyy, mm, dd, HH, MM, SS] = match;
    if (!HH)
        return new Date(`${yyyy}-${mm}-${dd}`);
    return new Date(`${yyyy}-${mm}-${dd}T${HH}:${MM}:${SS}`);
}
/**
 * Formats a duration so that it's readable by a normal user
 */
export function fmtDuration(ms) {
    let suffix = 'ms';
    let duration = ms;
    for (const [x, s] of [
        [1000, 's'],
        [60, 'm'],
        [60, 'h'],
        [24, 'd'],
        [7, 'w'],
    ]) {
        if (duration > x) {
            duration /= x;
            suffix = s;
        }
        else {
            break;
        }
    }
    return `${Math.round(100 * duration) / 100}${suffix}`;
}
/**
 * If the result is under limit return the remaining minutes
 * else return the hour with the minute
 */
export function getRemainingTime(datetime, now, limit) {
    const then = new Date(datetime);
    const isoParams = isoDateTime(datetime);
    const arrayDate = isoParams.split('T');
    const arrayHours = arrayDate[1].split(':');
    const nowDate = new Date(now);
    const difference = then.getTime() - nowDate.getTime();
    const result = Math.floor(difference / (1000 * 60));
    if (limit && result > limit) {
        return arrayHours[0] + 'h' + arrayHours[1];
    }
    else if (result <= 0) {
        return '';
    }
    else {
        return result;
    }
}
/**
 * Formats a size, given in bytes
 * Returns something like nSo where 1\<=n\<=1024, n has max 2 decimal digits and S is a suffix up to T
 */
export function fmtSize(size) {
    let suffix = '';
    ['k', 'M', 'G', 'T'].forEach(s => {
        if (size > 1024) {
            size /= 1024;
            suffix = s;
        }
    });
    return `${Math.round(size * 100) / 100}${suffix}o`;
}
/**
 * Formats a size, given in bytes
 * Returns something like n So where 1\<=n\<=1024, n has 2 decimal digits and S is a suffix up to T
 */
export function fmtSize2(size) {
    let suffix = '';
    ['k', 'M', 'G', 'T'].forEach(s => {
        if (size > 1024) {
            size /= 1024;
            suffix = s;
        }
    });
    return `${size.toFixed(2)} ${suffix}o`;
}
/**
 * Formats a phone number
 * Groups digits by pairs of 2
 */
export function fmtTel(tel) {
    tel = tel.replace(/[. -]/g, '');
    const prefix = tel.startsWith('+');
    if (prefix)
        tel = tel.substring(1);
    const telArr = Array.from(tel);
    for (let i = telArr.length - 2; i > 1; i -= 2)
        telArr.splice(i, 0, ' ');
    if (prefix)
        telArr.unshift('+');
    return telArr.join('');
}
/**
 * Apply case formatting to a text (see {@link CaseMode})
 */
export function fmtCase(text, caseMode) {
    switch (caseMode) {
        case 'lower':
            return text.toLocaleLowerCase();
        case 'upper':
            return text.toLocaleUpperCase();
        case 'rawLower':
            return text.toLowerCase();
        case 'rawUpper':
            return text.toUpperCase();
        case 'title':
            return text.replace(/\p{Letter}+/gu, x => fmtCase(x, 'capitalize'));
        case 'capitalize':
            return text
                .toLocaleLowerCase()
                .replace(/^\p{White_Space}*\p{Letter}/u, x => x.toLocaleUpperCase());
    }
}
/**
 * Format {@link Formattable} data with a {@link FmtMode}
 */
export function fmt(fmtMode, input) {
    switch (fmtMode ?? '') {
        case 'lower':
        case 'upper':
        case 'rawLower':
        case 'rawUpper':
        case 'title':
        case 'capitalize':
            if (typeof input === 'string' || typeof input === 'number')
                return fmtCase('' + input, fmtMode);
            else
                throw new Error('not a string');
        case 'date':
            return fmtDate(new Date(input));
        case 'time':
            return fmtTime(new Date(input));
        case 'datetime':
            return fmtDateTime(new Date(input));
        case 'isodate':
            return isoDate(new Date(input));
        case 'navitiadate':
            return navitiaDate(new Date(input));
        case 'duration':
            if (typeof input === 'string' || typeof input === 'number')
                return fmtDuration(+('' + input));
            else
                throw new Error('not a string');
        case 'size':
            if (typeof input === 'string' || typeof input === 'number')
                return fmtSize(+('' + input));
            else
                throw new Error('not a string');
        case 'size2':
            if (typeof input === 'string' || typeof input === 'number')
                return fmtSize2(+('' + input));
            else
                throw new Error('not a string');
        case 'tel':
            if (typeof input === 'string' || typeof input === 'number')
                return fmtTel('' + input);
            else
                throw new Error('not a string');
        case 'html':
            if (typeof input === 'string' || typeof input === 'number')
                return escapeHtml('' + input);
            else
                throw new Error('not a string');
        case 'url':
            if (typeof input === 'string' || typeof input === 'number')
                return encodeURIComponent('' + input);
            else
                throw new Error('not a string');
        case '':
            if (typeof input === 'string' || typeof input === 'number')
                return '' + input;
            else
                throw new Error('not a string');
        default:
            console.warn(`fmt('${fmtMode}', ${input instanceof Date
                ? isoDate(input)
                : JSON.stringify(input)}): invalid or unknown mode`);
            if (typeof input === 'string' || typeof input === 'number')
                return '' + input;
            else
                throw new Error('not a string');
    }
}
//# sourceMappingURL=data:application/json;base64,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